import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom-v5-compat';

import { scrollToTop } from 'platform/utilities/scroll';
import recordEvent from 'platform/monitoring/record-event';
import {
  CHAMPVA_PROVIDERS,
  claimAvailable,
} from '~/platform/shared/claims-appeals';

import { clearNotification as clearNotificationAction } from '../actions';
import ClaimDetailLayout from '../components/ClaimDetailLayout';
import { CLAIM_TABS, DEFAULT_CLAIM_TAB } from '../constants';
import { recordClaimTabChangeEvent } from '../utils/analytics';
import { searchWithTab } from '../utils/appeals-v2-helpers';
import {
  claimTabHref,
  knownClaimTab,
  labelForTab,
} from '../utils/claim-tab-helpers';
import { setTabDocumentTitle } from '../utils/helpers';
import { setUpPage } from '../utils/page';
import ClaimStatusPage from './ClaimStatusPage';
import FilesPage from './FilesPage';
import OverviewPage from './OverviewPage';

const TAB_CONTENT = {
  status: <ClaimStatusPage asTabPanel />,
  files: <FilesPage asTabPanel />,
  overview: <OverviewPage asTabPanel />,
};

export default function ClaimDetailsPage() {
  const claim = useSelector(
    state => state.disability.status.claimDetail.detail,
  );
  const loading = useSelector(
    state => state.disability.status.claimDetail.loading,
  );
  const message = useSelector(
    state => state.disability.status.notifications.message,
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const activeTab = knownClaimTab(searchParams.get('tab'));
  const canonical = activeTab !== null && !location.pathname.endsWith('/');

  const firstLoadHandledRef = useRef(false);
  const prevLoadingRef = useRef(loading);
  const entryHandledRef = useRef(false);

  // Entry focus: mirrors what each tab container does on mount (suppressed in
  // asTabPanel mode). Keyed on canonical rather than run-once because a
  // non-canonical entry redirects via a search-only <Navigate> that re-renders
  // this same instance; entryHandledRef keeps it entry-only. A cached-claim
  // entry deliberately leaves firstLoadHandledRef unconsumed so the refetch
  // edge from a subpage return still re-focuses the h1.
  useEffect(() => {
    if (!canonical || entryHandledRef.current) return;
    entryHandledRef.current = true;
    if (loading) {
      scrollToTop({ behavior: 'instant' });
    } else {
      setUpPage();
    }
  }, [canonical, loading]);

  // First loading edge only: later edges are in-view refreshes (post-upload
  // getClaim) where the success alert keeps focus.
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;
    if (!canonical || loading || !wasLoading) return;
    if (firstLoadHandledRef.current) return;
    firstLoadHandledRef.current = true;
    setUpPage(false, 'h1');
  }, [loading, canonical]);

  useEffect(() => {
    if (!canonical) return;
    if (claimAvailable(claim) || !loading) {
      setTabDocumentTitle(claim, labelForTab(activeTab));
    }
  }, [canonical, claim, loading, activeTab]);

  if (!canonical) {
    return (
      <Navigate
        to={{
          pathname: `/your-claims/${params.id}`,
          search: searchWithTab(
            location.search,
            activeTab ?? DEFAULT_CLAIM_TAB,
          ),
          hash: location.hash,
        }}
        replace
      />
    );
  }

  const typeParam = searchParams.get('type');
  const isChampva = CHAMPVA_PROVIDERS.has(typeParam);
  const handleTabChange = nextTabId => {
    recordClaimTabChangeEvent({ tab: nextTabId });
    if (isChampva) {
      recordEvent({
        event: 'nav-in-page',
        'nav-link-text': labelForTab(nextTabId),
        'nav-link-url': claimTabHref(params.id, nextTabId, {
          search: location.search,
        }),
      });
    }
    navigate({ search: searchWithTab(location.search, nextTabId) });
  };

  const tabsLabel = isChampva ? 'Application' : 'Claim';
  const tabPanels = CLAIM_TABS.map(tab => ({
    ...tab,
    content: tab.id === activeTab ? TAB_CONTENT[tab.id] : <></>,
  }));

  return (
    <ClaimDetailLayout
      claim={claim}
      loading={loading}
      message={message}
      clearNotification={() => dispatch(clearNotificationAction())}
      currentTab={labelForTab(activeTab)}
      tabPanels={tabPanels}
      activeTabId={activeTab}
      onTabChange={handleTabChange}
      tabsLabel={tabsLabel}
    />
  );
}
